import { computed, inject, Injectable, signal } from '@angular/core';
import * as sdk from 'matrix-js-sdk';
import { ApiHttpService } from './api.service'
import { UserNewsService } from './userNewsService'
import { ChatStorageService } from './chatStorage.service'
import { firstValueFrom } from 'rxjs';

@Injectable({
    providedIn: 'root'
}) export class MatrixService {
    apiService = inject(ApiHttpService);
    private chatStorage = inject(ChatStorageService);

    client: any;
    public hasNetwork = signal(navigator.onLine);
    public isOnline = signal(false);
    public messages = signal<any[]>([]);
    public rooms = signal<any[]>([]);
    public totalUnreadCount = computed(() => this.rooms().reduce(
        (total, room) => total + Math.max(0, Number(room?.unreadCount) || 0),
        0
    ));
    public netiquette = signal<any>(null);
    public typingUsers = signal<Record<string, string[]>>({});
    readCounts = signal<Record<string, { readBy: number; totalMembers: number }>>({});
    private readonly cachedImageUrls = new Map<string, string>();
    private readonly imageLoads = new Map<string, Promise<void>>();
    private readonly imageCacheVersion = signal(0);
    private readonly roomInformationCache = signal<Map<string, unknown>>(new Map());
    private readonly roomInformationRequests = new Map<string, Promise<unknown>>();
    private readonly roomInformationLoading = signal<Record<string, boolean>>({});

    private initialSyncPrepared = false;
    public roomsLoaded = signal(false);

    private roomsUpdateRunning = false;
    private roomsUpdateRequested = false;

    constructor() {
        window.addEventListener('online', () => this.hasNetwork.set(true));
        window.addEventListener('offline', () => {
            this.hasNetwork.set(false);
            this.isOnline.set(false);
            void this.loadOfflineRooms();
        });
    }

    async initClient(baseUrl: string, accessToken: string, userId: string) {
        this.roomsLoaded.set(false);
        this.initialSyncPrepared = false;
        console.log("Initializing Matrix client");
        if (this.client) {
            this.client.removeAllListeners();
            await this.client.stopClient();
            this.client = null;
            this.messages.set([]);
            this.rooms.set([]);
        }
        const domain = baseUrl.replace(/^https?:\/\//, '');
        this.client = sdk.createClient({
            baseUrl: baseUrl, accessToken: accessToken, userId: `@${userId}:${domain}`,
        });

        await this.client.startClient({initialSyncLimit: 30});

        this.listenForEvents();
        if (!navigator.onLine) {
            await this.loadOfflineRooms();
        }
    }


    private listenForEvents() {
        this.client.on("sync", async (state: string) => {
            if (state === 'PREPARED') {
                this.hasNetwork.set(true);
                this.isOnline.set(true);

                await this.requestRoomsUpdate('PREPARED');

                this.initialSyncPrepared = true;
                this.roomsLoaded.set(true);

                return;
            }

            if (state === 'SYNCING') {
                this.hasNetwork.set(true);
                this.isOnline.set(true);

                if (!this.initialSyncPrepared) {
                    return;
                }

                await this.requestRoomsUpdate('SYNCING');
                return;
            }
            if (state === 'ERROR' ||
                state === 'RECONNECTING' ||
                state === 'STOPPED') {
                this.isOnline.set(false);
                await this.loadOfflineRooms();
            }
        });

        this.client.on("Room", async () => {
            if (!this.initialSyncPrepared) {
                return;
            }

            await this.requestRoomsUpdate('Room');
        });

        this.client.on("Room.redaction", (event: any, room: any) => {
            const redactedEventId = event.getAssociatedId?.() ||
                event.event?.redacts ||
                event.getContent?.()?.redacts;

            if (!redactedEventId) {
                return;
            }
            this.messages.update(messages => messages.map(message => message.id === redactedEventId ? {
                ...message, deleted: true, body: '', filename: '', url: null, info: null
            } : message));

            void this.chatStorage.markMessageAsDeleted(redactedEventId);
        });

        this.client.on("Room.receipt", async (event: any, room: any) => {
            this.refreshReadCounts(room.roomId);
            if (!this.initialSyncPrepared) {
                return;
            }
            await this.requestRoomsUpdate('Room.receipt');
        });

        this.client.on("Room.timeline", (event: any, room: any, toStartOfTimeline: any) => {
            if (event.getType() === 'm.room.message') {
                if (room?.roomId) {
                    this.refreshReadCounts(room.roomId);
                }
            }
            if (event.getType() !== "m.room.message") return;

            const isDeleted = event.isRedacted();
            const content = event.getContent();

            const newMessage = {
                body: isDeleted ? '' : content.body,
                filename: isDeleted ? '' : content.filename || content.body,
                sender: event.sender.name,
                senderId: event.getSender(),
                timestamp: event.getTs(),
                roomId: room.roomId,
                msgtype: isDeleted ? 'm.text' : content.msgtype,
                subtype: isDeleted ? undefined : content['com.bytebrand.subtype'],
                url: isDeleted ? null : content.url,
                info: isDeleted ? null : content.info,
                id: event.getId()
            };
            void this.cacheMessage(newMessage).catch(error => {
                console.warn('Message cache failed:', newMessage.id, error);
            });
            if (content.msgtype === 'm.image' && content.url) {
                void this.ensureImageCached(this.mediaIdFromUrl(content.url));
            }
            this.messages.update(previous => {
                const existingIndex = previous.findIndex(message => message.id === newMessage.id);

                if (existingIndex !== -1) {
                    return previous.map(message => message.id === newMessage.id ? {...message, ...newMessage} : message);
                }

                return toStartOfTimeline ? [newMessage, ...previous] : [...previous, newMessage];
            });
        });

        this.client.on("Room.localEchoUpdated", (event: any, room: any, oldEventId: string) => {
            const newEventId = event.getId();
            if (oldEventId && newEventId && oldEventId !== newEventId) {
                this.messages.update(prev => {
                    const hasRemoteEcho = prev.some(msg => msg.id === newEventId);
                    if (hasRemoteEcho) {
                        return prev.filter(msg => msg.id !== oldEventId);
                    }

                    return prev.map(msg => msg.id === oldEventId ? {...msg, id: newEventId} : msg);
                });
                void this.chatStorage.replaceMessageEventId(oldEventId, newEventId).catch(error => {
                    console.warn('Message eventId cache update failed:', oldEventId, newEventId, error);
                });
                if (room?.roomId) {
                    this.refreshReadCounts(room.roomId);
                }
            }
        });

        this.client.on("RoomMember.typing", (_event: any, member: any) => {
            const roomId = member.roomId;

            const room = this.client.getRoom(roomId);
            if (!room) return;

            const typingMembers = room.currentState.getMembers()
                .filter((m: any) => m.typing && m.userId !== this.client.getUserId())
                .map((m: any) => m.name);

            this.typingUsers.update(v => ({
                ...v, [roomId]: typingMembers
            }));
        });
    }

    private async updateRoomsList() {
        const previousRooms = this.rooms();
        const roomInformationCache = this.roomInformationCache();
        const rooms = (await this.getRoomsWithDetails())
            .map((room: any) => {
                const previousRoom = previousRooms.find(item => item.roomId === room.roomId);
                if (previousRoom && Object.prototype.hasOwnProperty.call(previousRoom, 'eventInformation')) {
                    return {...room, eventInformation: previousRoom.eventInformation};
                }
                return roomInformationCache.has(room.roomId)
                    ? {...room, eventInformation: roomInformationCache.get(room.roomId)}
                    : room;
            })
            .sort((a: any, b: any) => {
                if (b.unreadCount !== a.unreadCount) {
                    return b.unreadCount - a.unreadCount;
                }
                return b.lastEvent - a.lastEvent;
            });
        this.rooms.set(rooms);
        await this.chatStorage.saveRooms(rooms);
        for (const room of rooms) {
            if (room.room_avatar) void this.ensureImageCached(room.room_avatar);
            if (room.lastMessage?.userAvatar) void this.ensureImageCached(room.lastMessage.userAvatar);
        }
        // console.log('Rooms updated:', rooms);
    }

    private async loadOfflineRooms() {
        const rooms = await this.chatStorage.getRooms();
        if (rooms.length) this.rooms.set(rooms);
    }

    getRoomInformation(roomId: string): unknown {
        const cachedInformation = this.roomInformationCache();
        if (cachedInformation.has(roomId)) {
            return cachedInformation.get(roomId);
        }
        const room = this.getRoom(roomId);
        if (room?.eventInformation != null) {
            return room.eventInformation;
        }
        return null;
    }

    isRoomInformationLoading(roomId: string): boolean {
        return !!this.roomInformationLoading()[roomId];
    }

    async loadRoomInformation(roomId: string): Promise<unknown> {
        if (!roomId) return null;
        const cachedInformation = this.roomInformationCache();
        if (cachedInformation.has(roomId)) {
            return cachedInformation.get(roomId);
        }
        const room = this.getRoom(roomId);
        if (room?.eventInformation != null) {
            this.cacheRoomInformation(roomId, room.eventInformation);
            return room.eventInformation;
        }

        const pendingRequest = this.roomInformationRequests.get(roomId);
        if (pendingRequest) return pendingRequest;

        if (!this.hasNetwork()) return null;

        this.setRoomInformationLoading(roomId, true);
        const request = firstValueFrom(this.apiService.get('userAction/eventInformation', {
            params: {roomId}
        }))
            .then((response: any) => response?.data ?? response)
            .then(async (information: unknown) => {
                this.cacheRoomInformation(roomId, information);
                this.rooms.update(rooms => rooms.map(currentRoom => currentRoom.roomId === roomId
                    ? {...currentRoom, eventInformation: information}
                    : currentRoom));
                await this.chatStorage.saveRooms(this.rooms());
                return information;
            })
            .finally(() => {
                this.roomInformationRequests.delete(roomId);
                this.setRoomInformationLoading(roomId, false);
            });

        this.roomInformationRequests.set(roomId, request);
        return request;
    }

    private cacheRoomInformation(roomId: string, information: unknown): void {
        this.roomInformationCache.update(current => {
            const next = new Map(current);
            next.set(roomId, information);
            return next;
        });
    }

    private setRoomInformationLoading(roomId: string, loading: boolean): void {
        this.roomInformationLoading.update(current => {
            const next = {...current};
            if (loading) {
                next[roomId] = true;
            } else {
                delete next[roomId];
            }
            return next;
        });
    }

    async loadCachedMessages(roomId: string) {
        const cached = await this.chatStorage.getMessages(roomId);
        const messages = cached.map(message => ({
            body: message.text,
            filename: message.filename || message.text,
            sender: message.sender,
            senderId: message.senderId,
            timestamp: message.timestamp,
            roomId: message.chatId,
            msgtype: message.msgtype,
            subtype: message.subtype,
            url: message.url,
            info: message.info,
            id: message.eventId ?? message.id,
            deleted: message.deleted ?? false
        }));
        this.messages.update(current => {
            const otherRooms = current.filter(message => message.roomId !== roomId);
            return [...otherRooms, ...messages].sort((a, b) => a.timestamp - b.timestamp);
        });
    }

    private async cacheMessage(message: any) {
        await this.chatStorage.addMessage({
            eventId: message.id,
            chatId: message.roomId,
            text: message.body ?? '',
            filename: message.filename,
            timestamp: message.timestamp,
            senderId: message.senderId ?? '',
            sender: message.sender,
            isRead: false,
            msgtype: message.msgtype,
            subtype: message.subtype,
            url: message.url,
            info: message.info,
            deleted: message.deleted ?? false
        });
    }

    async sendMessage(roomId: string, message: string, subType?: string) {
        let content: any = {
            body: message, msgtype: 'm.text'
        };
        if (subType) {
            content['com.bytebrand.subtype'] = subType;
        }
        return await this.client.sendEvent(roomId, 'm.room.message', content, '');
    }

    async sendTyping(roomId: string, typing: boolean) {
        if (!this.client) return;

        return this.client.sendTyping(roomId, typing, 30000);
    }

    getRooms() {
        if (!this.client) return [];
        return this.client.getRooms();
    }

    async getRoomsWithDetails() {
        if (!this.client) return [];
        const userId = this.client.getUserId();

        const rooms = this.client.getRooms().filter((room: any) => {
            const member = room.getMember(userId);
            return member && member.membership === 'join';
        });

        const results = [];


        for (const room of rooms) {
            let events = room.getLiveTimeline().getEvents();
            let lastMessageEvent = [...events].reverse().find((event: any) => event.getType() === 'm.room.message'
                && !!event?.getContent()?.body);

            if (!lastMessageEvent) {
                try {
                    await this.client.scrollback(room, 30);
                    events = room.getLiveTimeline().getEvents();
                    lastMessageEvent = [...events].reverse().find((event: any) => event.getType() === 'm.room.message'
                    && !!event?.getContent()?.body);
                } catch (e) {
                    console.warn('scrollback failed for room', room.roomId, e);
                }
            }

            for (const event of events) {
                if (event.getType() !== 'm.room.message') {
                    continue;
                }
                const isDeleted = event.isRedacted();
                const content = event.getContent();
                const message = {
                    body: isDeleted ? '' : content.body,
                    filename: isDeleted ? '' : content.filename || content.body,
                    sender: event.sender?.name || event.getSender(),
                    senderId: event.getSender(),
                    timestamp: event.getTs(),
                    roomId: room.roomId,
                    msgtype: isDeleted ? 'm.text' : content.msgtype,
                    subtype: isDeleted ? undefined : content['com.bytebrand.subtype'],
                    url: isDeleted ? null : content.url,
                    info: isDeleted ? null : content.info,
                    id: event.getId(),
                    deleted: isDeleted
                };
                void this.cacheMessage(message).catch(error => {
                    console.warn('Message cache failed:', message.id, error);
                });
                if (content.msgtype === 'm.image' && content.url) {
                    void this.ensureImageCached(this.mediaIdFromUrl(content.url));
                }
            }

            let initials = '';
            let userAvatar = '';
            if (lastMessageEvent?.sender?.name) {
                const sender = lastMessageEvent.sender.name;
                initials = sender.split(' ').map((word: any) => word[0]).join('');
                const mxcAvatarUrl = lastMessageEvent.sender.getMxcAvatarUrl();
                if (mxcAvatarUrl) {
                    userAvatar = mxcAvatarUrl.split('/').pop();
                }
            }

            const colorEvent = room.currentState.getStateEvents('m.room.color', '');
            const typeEvent = room.currentState.getStateEvents('m.room.type', '');
            const roomColor = colorEvent ? colorEvent.getContent().room_color : null;
            const roomType = typeEvent ? typeEvent.getContent().type : null;
            const mxcUrl = room.getMxcAvatarUrl();
            let avatarUrl = null;
            if (mxcUrl) {
                avatarUrl = mxcUrl.split('/').pop();
            }

            results.push({
                roomId: room.roomId, name: room.name, room_avatar: avatarUrl,
                room_color: roomColor, room_type: roomType,
                lastEvent: events.length ? events[events.length - 1].getTs() : 0,
                unreadCount: room.getUnreadNotificationCount('total'),
                lastMessage: lastMessageEvent ? {
                    body: lastMessageEvent.getContent().body,
                    sender: lastMessageEvent.sender ? lastMessageEvent.sender.name : lastMessageEvent.getSender(),
                    timestamp: lastMessageEvent.getTs(),
                    initials, userAvatar
                } : null
            });
        }

        return results;
    }

    getCurrentUserId() {
        return this.client ? this.client.getUserId() : null;
    }

    getRoom(roomId: string) {
        return this.rooms().find((room: any) => room.roomId === roomId)
    }

    async loadMoreMessages(roomId: string): Promise<boolean> {
        if (!this.client) return false;

        const room = this.client.getRoom(roomId);
        if (!room) return false;

        const beforeCount = room.getLiveTimeline().getEvents().length;

        await this.client.scrollback(room, 20);

        const afterCount = room.getLiveTimeline().getEvents().length;
        return afterCount > beforeCount;
    }

    getRoomMembers(roomId: string): string[] {
        if (!this.client) return [];

        const room = this.client.getRoom(roomId);
        if (!room) return [];
        const events = room?.currentState.getStateEvents('com.ibexfusion.user_metadata') || [];
        const usersMetadata = events.map((event: any) => ({
            matrixUserId: event.getStateKey(), metadata: event.getContent(),
        }));
        const members = room.getJoinedMembers();
        return members
            .filter((member: any) => member.powerLevel < 100)
            .map((member: any) => {
                const mxcAvatarUrl = member.getMxcAvatarUrl();
                let avatarUrl = null;
                if (mxcAvatarUrl) {
                    avatarUrl = mxcAvatarUrl.split('/').pop();
                }
                let initials = '';
                if (member.name) {
                    initials = member.name.split(' ').slice(0, 2).map((word: any) => word.charAt(0)).join('');
                }
                const userMetadata = usersMetadata.find((metadata: any) => metadata.matrixUserId === this.extractUsername(member.userId));
                return {
                    name: member.name,
                    avatar_url: avatarUrl,
                    initials,
                    metadata: userMetadata?.metadata,
                    powerLevel: member.powerLevel,
                    id: member.userId
                };
            });
    }

    extractUsername(matrixId: string): string {
        if (!matrixId) return '';
        return matrixId.split(':')[0].replace('@', '');
    }

    getMedia(mediaId: string) {
        this.imageCacheVersion();
        const online = this.isOnline();
        const normalizedMediaId = this.mediaIdFromUrl(mediaId);
        if (!normalizedMediaId) return '';

        const cachedUrl = this.cachedImageUrls.get(normalizedMediaId);
        if (cachedUrl) return cachedUrl;

        void this.ensureImageCached(normalizedMediaId);
        return online ? this.getRemoteMediaUrl(normalizedMediaId) : '';
    }

    private mediaIdFromUrl(url: string): string {
        return url?.split('/').pop() ?? '';
    }

    private getRemoteMediaUrl(mediaId: string): string {
        const accessToken = this.client?.getAccessToken();
        if (!accessToken) return '';
        return this.apiService.apiBaseUrl + 'matrix/media/' + encodeURIComponent(mediaId)
            + '?access_token=' + encodeURIComponent(accessToken);
    }

    private ensureImageCached(mediaId: string): Promise<void> {
        if (!mediaId || this.cachedImageUrls.has(mediaId)) return Promise.resolve();

        const existingLoad = this.imageLoads.get(mediaId);
        if (existingLoad) return existingLoad;

        const load = this.loadOrDownloadImage(mediaId)
            .catch(error => console.warn('Image cache failed:', mediaId, error))
            .finally(() => this.imageLoads.delete(mediaId));
        this.imageLoads.set(mediaId, load);
        return load;
    }

    private async loadOrDownloadImage(mediaId: string) {
        const cached = await this.chatStorage.getImage(mediaId);
        if (cached?.blob) {
            this.setCachedImageUrl(mediaId, cached.blob);
            return;
        }

        if (!this.isOnline()) return;
        const remoteUrl = this.getRemoteMediaUrl(mediaId);
        if (!remoteUrl) return;

        const response = await fetch(remoteUrl);
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const blob = await response.blob();
        await this.chatStorage.saveImage(mediaId, blob);
        this.setCachedImageUrl(mediaId, blob);
    }

    private setCachedImageUrl(mediaId: string, blob: Blob) {
        this.cachedImageUrls.set(mediaId, URL.createObjectURL(blob));
        this.imageCacheVersion.update(version => version + 1);
    }

    deleteMsg(roomId: string, msgId: string) {
        return this.apiService.get('matrix/deleteMsg/' + msgId + '?roomId=' + roomId + '&access_token=' + this.client.getAccessToken());
    }

    private getMimeTypeFromFilename(filename: string): string {
        const extension = filename
            .toLowerCase()
            .split('.')
            .pop();

        const mimeTypes: Record<string, string> = {
            jpg: 'image/jpeg',
            jpeg: 'image/jpeg',
            png: 'image/png',
            webp: 'image/webp',
            heic: 'image/heic',
            heif: 'image/heif',

            pdf: 'application/pdf',
            txt: 'text/plain',

            doc: 'application/msword',
            docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',

            xls: 'application/vnd.ms-excel',
            xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',

            ppt: 'application/vnd.ms-powerpoint',
            pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
        };

        return mimeTypes[extension ?? '']
            ?? 'application/octet-stream';
    }

    async sendFile(roomId: string, file: File, comment = '') {
        if (!this.client) {
            throw new Error('Matrix client is not initialized');
        }

        console.log('Matrix upload input:', {
            name: file.name,
            type: file.type,
            size: file.size,
        });

        const arrayBuffer = await file.arrayBuffer();

        if (!arrayBuffer.byteLength) {
            throw new Error(`File "${file.name}" could not be read`);
        }

        const mimeType = file.type || this.getMimeTypeFromFilename(file.name);

        const blob = new Blob(
            [arrayBuffer],
            {
                type: mimeType,
            }
        );

        if (!blob.size) {
            throw new Error(`File "${file.name}" is empty`);
        }

        console.log('Matrix upload blob:', {
            name: file.name,
            originalSize: file.size,
            blobSize: blob.size,
            mimeType,
        });

        const uploadResponse = await this.client.uploadContent(blob, {
            name: file.name,
            type: mimeType,
        });

        const mxcUrl = uploadResponse.content_uri;

        if (!mxcUrl) {
            throw new Error('Matrix upload returned no content_uri');
        }

        let msgtype = 'm.file';

        if (mimeType.startsWith('image/')) {
            msgtype = 'm.image';
        } else if (mimeType.startsWith('video/')) {
            msgtype = 'm.video';
        } else if (mimeType.startsWith('audio/')) {
            msgtype = 'm.audio';
        }

        const content: any = {
            filename: file.name,
            body: comment.trim() || file.name,
            msgtype,
            url: mxcUrl,
            info: {
                mimetype: mimeType,
                size: blob.size,
            },
        };

        return await this.client.sendEvent(
            roomId,
            'm.room.message',
            content,
            ''
        );
    }

    getRoomMedia(roomId: string) {
        if (!this.client) return {media: [], documents: []};

        const room = this.client.getRoom(roomId);
        if (!room) return {media: [], documents: []};

        const events = room.getLiveTimeline().getEvents();
        const mediaTypes = ['m.image', 'm.video', 'm.audio', 'm.file'];

        const items = events
            .filter((event: any) => event.getType() === 'm.room.message')
            .filter((event: any) => !event.isRedacted())
            .filter((event: any) => mediaTypes.includes(event.getContent().msgtype))
            .map((event: any) => {
                const content = event.getContent();
                const mediaId = content.url ? content.url.split('/').pop() : null;
                return {
                    body: content.body,
                    filename: content.filename || content.body,
                    msgtype: content.msgtype,
                    mxcUrl: content.url,
                    mediaId,
                    downloadUrl: mediaId ? this.getRemoteMediaUrl(mediaId) : null,
                    info: content.info,
                    sender: event.sender ? event.sender.name : event.getSender(), timestamp: event.getTs(),
                    senderId: event?.sender?.userId
                };
            });

        return {
            media: items.filter((i: any) => i.msgtype === 'm.image' || i.msgtype === 'm.video'),
            documents: items.filter((i: any) => i.msgtype === 'm.file' || i.msgtype === 'm.audio'),
            all: items,
        };
    }

    async loadAllRoomMedia(roomId: string) {
        if (!this.client) return {media: [], documents: [], all: []};

        const room = this.client.getRoom(roomId);
        if (!room) return {media: [], documents: [], all: []};

        const timeline = room.getLiveTimeline();

        let hasMore = true;
        let safety = 0;
        while (hasMore && safety < 100) {
            hasMore = await this.client.paginateEventTimeline(timeline, {
                backwards: true, limit: 50,
            });
            safety++;
        }

        return this.getRoomMedia(roomId);
    }

    getMyPowerLevel(roomId: string): number {
        if (!this.client) return 0;

        const room = this.client.getRoom(roomId);
        if (!room) return 0;

        const userId = this.client.getUserId(); // поточний користувач
        const member = room.getMember(userId);

        return member?.powerLevel ?? 0;
    }

    async stopClient() {
        if (this.client) {
            this.client.removeAllListeners();
            await this.client.stopClient();
            this.client = null;
        }
        this.roomsLoaded.set(false);
        this.initialSyncPrepared = false;
        this.messages.set([]);
        this.rooms.set([]);
        this.roomInformationCache.set(new Map());
        this.roomInformationRequests.clear();
        this.roomInformationLoading.set({});
    }

    async markRoomAsRead(roomId: string) {
        if (!this.client) return;

        const room = this.client.getRoom(roomId);
        if (!room) return;

        const lastEvent = [...room.getLiveTimeline().getEvents()]
            .reverse()
            .find((event: any) => {
                const eventId = event.getId();
                return event.getType() === 'm.room.message'
                    && !event.isRedacted()
                    && typeof eventId === 'string'
                    && eventId.startsWith('$');
            });

        if (lastEvent) {
            await this.client.sendReadReceipt(lastEvent);
            await this.requestRoomsUpdate('markRoomAsRead');
        }
    }

    getUnreadCount(roomId: string): number {
        if (!this.client) return 0;

        const room = this.client.getRoom(roomId);
        if (!room) return 0;

        return room.getUnreadNotificationCount('total');
    }

    isRoomFullyRead(roomId: string): boolean {
        return this.getUnreadCount(roomId) === 0;
    }

    getReadCountsForLastMessages(roomId: string, limit = 20): Array<{
        messageId: string; readBy: number; totalMembers: number;
    }> {
        if (!this.client) return [];

        const room = this.client.getRoom(roomId);
        if (!room) return [];

        const currentUserId = this.client.getUserId();

        const members = room.getJoinedMembers().filter((m: any) => m.userId !== currentUserId && +m.powerLevel !== 100);
        const totalMembers = members.length;

        const events = room.getLiveTimeline().getEvents()
            .filter((e: any) => e.getType() === 'm.room.message' &&
                !e.isRedacted() && e.getSender() === currentUserId
            )
            .slice(-limit);

        const memberReadEventIds = members.map((member: any) => {
            return room.getEventReadUpTo(member.userId);
        });

        const allEvents = room.getLiveTimeline().getEvents();

        return events.map((event: any) => {
            const eventIndex = allEvents.findIndex((e: any) => e.getId() === event.getId());

            let readBy = 0;
            for (const readEventId of memberReadEventIds) {
                if (!readEventId) continue;
                const readIndex = allEvents.findIndex((e: any) => e.getId() === readEventId);
                if (readIndex >= eventIndex) {
                    readBy++;
                }
            }

            return {
                messageId: event.getId(), readBy, totalMembers,
            };
        });
    }

    refreshReadCounts(roomId: string) {
        const counts = this.getReadCountsForLastMessages(roomId);
        const map: Record<string, { readBy: number; totalMembers: number }> = {};
        for (const item of counts) {
            map[item.messageId] = {readBy: item.readBy, totalMembers: item.totalMembers};
        }
        this.readCounts.set(map);
    }

    getMyLastReadEventId(roomId: string): string | null {
        if (!this.client) return null;

        const room = this.client.getRoom(roomId);
        if (!room) return null;

        const userId = this.client.getUserId();
        if (!userId) return null;

        return room.getEventReadUpTo(userId) ?? null;
    }

    private async requestRoomsUpdate(source: string): Promise<void> {
        //  console.log(
        //     'ROOM UPDATE:',
        //     source,
        //     'prepared:', this.initialSyncPrepared,
        //     'matrix rooms:', this.client?.getRooms()?.length
        // );
        if (this.roomsUpdateRunning) {
            this.roomsUpdateRequested = true;
            return;
        }

        this.roomsUpdateRunning = true;

        try {
            do {
                this.roomsUpdateRequested = false;

                await this.updateRoomsList();

            } while (this.roomsUpdateRequested);
        } finally {
            this.roomsUpdateRunning = false;
        }
    }
}
